import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import {
  UserCircle, Lock, Camera, Eye, EyeSlash, Check, EnvelopeSimple,
} from '@phosphor-icons/react';
import AppFooter from '../components/AppFooter';
import { getAvatarStyle, getInitials } from '../lib/avatarColor';

// Personal account page — visible to EVERY signed-in user regardless of role.
// Lets them edit their display name, upload an avatar, and change their
// password. Workspace-scoped settings (company profile, GST, billing) live on
// /settings and are gated to owner/admin only.
export default function AccountPage() {
  const { user, getOrgHeaders, API, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [uploading, setUploading] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user?.name]);

  const headers = () => ({ withCredentials: true, headers: getOrgHeaders() });

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Avatar must be 5 MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, headers());
      const url = r.data?.url;
      if (!url) throw new Error('upload missing url');
      const fullUrl = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
      await axios.put(`${API}/auth/me`, { avatar: fullUrl }, headers());
      await refreshUser();
      toast.success('Profile picture updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile picture');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemoveAvatar = async () => {
    setUploading(true);
    try {
      await axios.put(`${API}/auth/me`, { avatar: null }, headers());
      await refreshUser();
      toast.success('Profile picture removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove profile picture');
    } finally { setUploading(false); }
  };

  const handleSaveProfile = async () => {
    if (!name.trim()) {
      toast.error('Name cannot be empty');
      return;
    }
    setSavingProfile(true);
    try {
      await axios.put(`${API}/auth/me`, { name: name.trim() }, headers());
      await refreshUser();
      setProfileSaved(true);
      setTimeout(() => setProfileSaved(false), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save profile');
    } finally { setSavingProfile(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error('All password fields are required');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirmation do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/auth/change-password`,
        { current_password: currentPassword, new_password: newPassword },
        headers()
      );
      toast.success('Password updated');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password');
    } finally { setChangingPassword(false); }
  };

  const avatarFallback = getAvatarStyle(user?.user_id || user?.email || 'me');

  return (
    <div className="flex-1 overflow-y-auto bg-zinc-50/40" data-testid="account-page">
      <div className="bg-white border-b border-zinc-200 px-8 py-5">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-['Outfit'] text-[22px] font-bold text-zinc-900 tracking-tight">My Account</h1>
          <p className="text-[12px] text-zinc-500 mt-1">Personal profile, sign-in &amp; password — applies across every workspace you're a member of.</p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 py-8 space-y-5">
        {/* Profile card */}
        <section className="bg-white rounded-xl border border-zinc-200/70 overflow-hidden" data-testid="account-profile-card">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white">
              <UserCircle size={16} weight="bold" />
            </div>
            <div>
              <h2 className="text-[14px] font-bold text-zinc-900">Profile</h2>
              <p className="text-[11px] text-zinc-500">Your name, photo, and login email.</p>
            </div>
          </div>
          <div className="p-6 space-y-5">
            {/* Avatar uploader */}
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 ring-2 ring-zinc-200">
                {user?.avatar && <AvatarImage src={user.avatar} alt={user?.name} />}
                <AvatarFallback className={`${avatarFallback.bg} text-white text-[16px] font-bold`}>
                  {getInitials(user?.name || user?.email)}
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1.5">
                <p className="text-[13px] font-semibold text-zinc-800">Profile picture</p>
                <p className="text-[11px] text-zinc-500">PNG or JPG · max 5 MB · auto-compressed.</p>
                <div className="flex items-center gap-2 pt-1">
                  <label className="inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-[12px] font-medium bg-zinc-900 text-white hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50" data-testid="account-avatar-upload-label">
                    <Camera size={13} weight="bold" />
                    {uploading ? 'Uploading…' : 'Upload'}
                    <input type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} disabled={uploading} data-testid="account-avatar-upload" />
                  </label>
                  {user?.avatar && (
                    <Button variant="ghost" size="sm" onClick={handleRemoveAvatar} disabled={uploading} className="h-8 text-[12px] text-zinc-500" data-testid="account-avatar-remove">
                      Remove
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Display name</Label>
              <Input
                value={name}
                onChange={e => setName(e.target.value)}
                maxLength={100}
                placeholder="Your name"
                className="mt-1.5 h-10"
                data-testid="account-name-input"
              />
              <p className="text-[11px] text-zinc-400 mt-1">Visible to teammates across all your workspaces.</p>
            </div>

            <div>
              <Label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Email</Label>
              <div className="mt-1.5 flex items-center gap-2 h-10 px-3 rounded-md bg-zinc-50 border border-zinc-200">
                <EnvelopeSimple size={14} className="text-zinc-400" />
                <span className="text-[13px] text-zinc-700 truncate">{user?.email}</span>
                <span className="ml-auto text-[10px] text-zinc-400 uppercase tracking-wider">Read-only</span>
              </div>
              <p className="text-[11px] text-zinc-400 mt-1">Email is your sign-in identity and can't be changed in-app. Contact support if you need to migrate.</p>
            </div>

            <div className="flex items-center justify-end pt-1">
              {profileSaved && (
                <span className="inline-flex items-center gap-1 text-[12px] text-emerald-600 font-medium mr-3" data-testid="account-profile-saved">
                  <Check size={13} weight="bold" /> Saved
                </span>
              )}
              <Button
                onClick={handleSaveProfile}
                disabled={savingProfile || !name.trim() || name === user?.name}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="account-save-profile"
              >
                {savingProfile ? 'Saving…' : 'Save profile'}
              </Button>
            </div>
          </div>
        </section>

        {/* Password card */}
        <section className="bg-white rounded-xl border border-zinc-200/70 overflow-hidden" data-testid="account-password-card">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-rose-500 to-red-600 flex items-center justify-center text-white">
              <Lock size={16} weight="bold" />
            </div>
            <div>
              <h2 className="text-[14px] font-bold text-zinc-900">Password</h2>
              <p className="text-[11px] text-zinc-500">Update the password used to sign in to Inspiro Projects.</p>
            </div>
          </div>
          <form onSubmit={handleChangePassword} className="p-6 space-y-4">
            <div>
              <Label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Current password</Label>
              <div className="relative mt-1.5">
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={e => setCurrentPassword(e.target.value)}
                  className="h-10 pr-10"
                  data-testid="account-current-password"
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowCurrent(s => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
                  {showCurrent ? <EyeSlash size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">New password</Label>
                <div className="relative mt-1.5">
                  <Input
                    type={showNew ? 'text' : 'password'}
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    className="h-10 pr-10"
                    placeholder="At least 6 characters"
                    data-testid="account-new-password"
                    autoComplete="new-password"
                  />
                  <button type="button" onClick={() => setShowNew(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
                    {showNew ? <EyeSlash size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-[11px] font-semibold text-zinc-600 uppercase tracking-wide">Confirm new password</Label>
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  className="h-10 mt-1.5"
                  placeholder="Repeat new password"
                  data-testid="account-confirm-password"
                  autoComplete="new-password"
                />
              </div>
            </div>
            <div className="flex items-center justify-end pt-1">
              <Button
                type="submit"
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                className="bg-rose-600 hover:bg-rose-700"
                data-testid="account-change-password-submit"
              >
                {changingPassword ? 'Updating…' : 'Update password'}
              </Button>
            </div>
          </form>
        </section>

        {/* Workspaces summary — read-only list of orgs the user belongs to */}
        <section className="bg-white rounded-xl border border-zinc-200/70 overflow-hidden" data-testid="account-workspaces-card">
          <div className="px-6 py-4 border-b border-zinc-100">
            <h2 className="text-[14px] font-bold text-zinc-900">Your workspaces</h2>
            <p className="text-[11px] text-zinc-500">All Inspiro Projects workspaces you're a member of, with your role in each. Switch from the user pill in the sidebar.</p>
          </div>
          <div className="divide-y divide-zinc-100">
            {(user?.memberships || []).map(m => (
              <div key={m.org_id} className="px-6 py-3 flex items-center gap-3" data-testid={`account-workspace-${m.org_id}`}>
                <div className="w-8 h-8 rounded-md bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0">
                  {m.org_name?.charAt(0)?.toUpperCase() || 'W'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold text-zinc-800 truncate">{m.org_name || 'Unknown workspace'}</p>
                  <p className="text-[11px] text-zinc-500">Joined {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}</p>
                </div>
                <span className="text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md bg-zinc-100 text-zinc-700">
                  {(m.role || 'member').replace('_', ' ')}
                </span>
              </div>
            ))}
            {(!user?.memberships || user.memberships.length === 0) && (
              <p className="px-6 py-6 text-[12px] text-zinc-400 italic">You're not a member of any workspace yet.</p>
            )}
          </div>
        </section>
      </div>
      <AppFooter />
    </div>
  );
}
